import { IPatient } from "../../../requester/patient/patient.types";
import { MedicalRequestContent, MedicalRequestContentItem } from "../../../requester/request/request.models";
import { IClinician, IMedicalRequest, IRequestStatusDates, RequestStatus } from "../../../requester/request/request.types";
import { requestTypeLabel } from '../../../request-type.labels';


/**
 * Provides computed properties for UI display and expert operations
 */
export class ExpertRequest implements Required<IMedicalRequest> {
    // Core properties from API - matching OpenAPI spec exactly
    entityId: string;
    zTouchTimestamp: string | null;
    zCreationTimestamp: string | null;
    status: RequestStatus;
    speciality: string;
    requestType: string;
    content: MedicalRequestContent;
    isRead: boolean;
    clinicianId: string;
    patientId: string;
    handleBy: string;
    clinician: IClinician | null;
    patient: IPatient | null;
    expertAssigned: IClinician | null;
    requestStatusDates: IRequestStatusDates | null;
    ztouchTimestamp: string;
    zcreationTimestamp: string;

    /**
     * Returns request title for display
     */
    get title(): string {
        const patientName = this.patientFullName;
        const typeDisplay = requestTypeLabel(this.requestType);
        return `${typeDisplay} - ${patientName}`;
    }

    /**
     * Returns request display name with patient info
     */
    get displayName(): string {
        return `${this.requestType} for ${this.patientFullName}`;
    }

    /**
     * Returns request type display name
     */
    get requestTypeDisplay(): string {
        return requestTypeLabel(this.requestType);
    }

    /**
     * Returns speciality display name
     */
    get specialityDisplay(): string {
        return this.speciality;
    }

    /**
     * Returns status display name
     */
    get statusDisplay(): string {
        switch (this.status) {
            case 'DRAFT':
                return 'Draft';
            case 'WAITING':
                return 'Waiting';
            case 'IN_PROGRESS':
                return 'In Progress';
            case 'COMPLETED':
                return 'Completed';
            case 'ARCHIVED':
                return 'Archived';
            default:
                return this.status;
        }
    }

    /**
     * Returns status color class for UI
     */
    get statusColor(): string {
        switch (this.status) {
            case 'DRAFT':
                return 'text-gray-600 bg-gray-100';
            case 'WAITING':
                return 'text-yellow-600 bg-yellow-100';
            case 'IN_PROGRESS':
                return 'text-blue-600 bg-blue-100';
            case 'COMPLETED':
                return 'text-green-600 bg-green-100';
            case 'ARCHIVED':
                return 'text-gray-500 bg-gray-50';
            default:
                return 'text-gray-600 bg-gray-100';
        }
    }

    /**
     * Returns request type icon
     */
    get requestTypeIcon(): string {
        const type = this.requestType.toLowerCase();
        switch (type) {
            case 'ecg':
                return 'heroicons_outline:heart';
            case 'holter':
                return 'heroicons_outline:device-phone-mobile';
            case 'dermatology_photos':
                return 'heroicons_outline:camera';
            default:
                return 'heroicons_outline:document';
        }
    }

    /**
     * Returns speciality icon
     */
    get specialityIcon(): string {
        const speciality = this.speciality.toLowerCase();
        switch (speciality) {
            case 'cardiology':
                return 'heroicons_outline:heart';
            case 'dermatology':
                return 'heroicons_outline:camera';
            default:
                return 'heroicons_outline:document';
        }
    }

    /**
     * Returns patient full name - NO MORE ANONYMIZATION
     */
    get patientFullName(): string {
        if (!this.patient) {
            return `Patient ${this.patientId.substring(0, 8)}...`;
        }
        
        const firstName = this.patient.firstName || '';
        const lastName = this.patient.lastName || '';
        return firstName ? `${firstName} ${lastName}` : lastName;
    }

    /**
     * Returns clinician full name
     */
    get clinicianFullName(): string {
        if (!this.clinician) {
            return `Clinician ${this.clinicianId.substring(0, 8)}...`;
        }
        
        const firstName = this.clinician.firstName || '';
        const lastName = this.clinician.lastName || '';
        return firstName ? `${firstName} ${lastName}` : lastName;
    }

    /**
     * Returns patient age if available
     */
    get patientAge(): number | null {
        if (!this.patient?.birthDate) return null;

        const today = new Date();
        const birthDate = new Date(this.patient.birthDate);
        let age = today.getFullYear() - birthDate.getFullYear();
        const monthDiff = today.getMonth() - birthDate.getMonth();

        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            age--;
        }

        return Math.max(0, age);
    }

    /**
     * Returns formatted patient age with proper units (days, weeks, months, years)
     * If patient.birthDate is available, uses intelligent formatting (days/weeks/months/years)
     * Otherwise falls back to patientAge property (years only)
     */
    get patientAgeFormatted(): string {
        // Try to use patient.birthDate for intelligent formatting
        if (this.patient?.birthDate) {
            const birthDate = new Date(this.patient.birthDate);
            const today = new Date();
            const diffTime = today.getTime() - birthDate.getTime();
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

            // Less than 7 days: show days
            if (diffDays < 7) {
                const ageText = diffDays === 0 ? 'Nouveau-né' : diffDays === 1 ? '1 jour' : `${diffDays} jours`;
                return `Âge : ${ageText}`;
            }

            // Less than 8 weeks (56 days): show weeks
            if (diffDays < 56) {
                const weeks = Math.floor(diffDays / 7);
                const ageText = weeks === 1 ? '1 semaine' : `${weeks} semaines`;
                return `Âge : ${ageText}`;
            }

            // Calculate months
            let months = (today.getFullYear() - birthDate.getFullYear()) * 12;
            months += today.getMonth() - birthDate.getMonth();
            if (today.getDate() < birthDate.getDate()) {
                months--;
            }
            months = Math.max(0, months);

            // Less than 24 months: show months
            if (months < 24) {
                const ageText = months === 1 ? '1 mois' : `${months} mois`;
                return `Âge : ${ageText}`;
            }

            // 24 months and above: show years
            const age = this.patientAge;
            if (age !== null) {
                const ageText = age === 1 ? '1 an' : `${age} ans`;
                return `Âge : ${ageText}`;
            }
        }

        // Fallback: check if we have a numeric patientAge property (from API)
        // This happens when patient object is not fully loaded but age is available
        if (this.patientAge !== null && this.patientAge !== undefined) {
            const ageText = this.patientAge === 1 ? '1 an' : `${this.patientAge} ans`;
            return `Âge : ${ageText}`;
        }

        return 'Âge inconnu';
    }

    /**
     * Returns formatted creation date
     */
    get createdAtFormatted(): string {
        if (!this.zcreationTimestamp) return 'Unknown';
        
        try {
            const date = new Date(this.zcreationTimestamp);
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
        } catch {
            return 'Invalid date';
        }
    }

    /**
     * Returns formatted modification date
     */
    get modifiedAtFormatted(): string {
        if (!this.ztouchTimestamp) return 'Unknown';
        
        try {
            const date = new Date(this.ztouchTimestamp);
            return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
        } catch {
            return 'Invalid date';
        }
    }

    /**
     * Returns the primary ID
     */
    get id(): string {
        return this.entityId;
    }

    /**
     * Returns request summary/description from content
     */
    get summary(): string {
        const messageItem = this.content.items.find(item => item.name === 'message');
        return messageItem?.value || 'No description available';
    }

    /**
     * Returns whether request can be taken charge of (for pending requests)
     */
    get canTakeCharge(): boolean {
        return this.status === 'WAITING';
    }

    /**
     * Returns whether expert can give opinion (for contributions)
     */
    get canGiveOpinion(): boolean {
        return this.status === 'IN_PROGRESS';
    }

    /**
     * Returns whether request is completed
     */
    get isCompleted(): boolean {
        return this.status === 'COMPLETED';
    }

    /**
     * Returns whether request is in progress
     */
    get isInProgress(): boolean {
        return this.status === 'IN_PROGRESS';
    }

    /**
     * Returns text content items
     */
    get textItems(): MedicalRequestContentItem[] {
        return this.content.items.filter(item => item.type === 'TEXT');
    }

    /**
     * Returns file content items
     */
    get fileItems(): MedicalRequestContentItem[] {
        return this.content.items.filter(item => item.type === 'FILE');
    }

    /**
     * Constructor - initializes ExpertRequest from API response
     */
    constructor(request: IMedicalRequest) {
        // Required fields
        this.entityId = request.entityId;
        this.status = request.status;
        this.speciality = request.speciality;
        this.requestType = request.requestType;
        this.content = new MedicalRequestContent(request.content);
        this.clinicianId = request.clinicianId;
        this.patientId = request.patientId;
        this.ztouchTimestamp = request.ztouchTimestamp;
        this.zcreationTimestamp = request.zcreationTimestamp;
        
        // Optional fields with defaults
        this.zTouchTimestamp = request.zTouchTimestamp || null;
        this.zCreationTimestamp = request.zCreationTimestamp || null;
        this.isRead = request.isRead || false;
        this.handleBy = request.handleBy || '';
        this.clinician = request.clinician || null;
        this.patient = request.patient || null;
        this.expertAssigned = request.expertAssigned || null;
        this.requestStatusDates = request.requestStatusDates || null;
    }

    /**
     * Returns timeline events based on request status dates
     */
    getTimelineEvents(): Array<{status: RequestStatus | 'CREATION' | 'MODIFICATION', date: string, label: string}> {
        const events: Array<{status: RequestStatus | 'CREATION' | 'MODIFICATION', date: string, label: string}> = [];

        // Always add creation event (from expert perspective, it's when they received it)
        events.push({
            status: 'CREATION',
            date: this.zcreationTimestamp,
            label: 'Demande créée'
        });

        // Add status transition events if available
        if (this.requestStatusDates) {
            const statusLabels: Record<RequestStatus, string> = {
                'DRAFT': 'Brouillon créé',
                'WAITING': 'Demande reçue',
                'IN_PROGRESS': 'Prise en charge',
                'COMPLETED': 'Avis fourni',
                'ARCHIVED': 'Demande archivée'
            };

            // Add events for all non-null status dates
            Object.entries(this.requestStatusDates).forEach(([status, date]) => {
                if (date) {
                    events.push({
                        status: status as RequestStatus,
                        date: date,
                        label: statusLabels[status as RequestStatus]
                    });
                }
            });
        }

        // Add modification event only if it's meaningfully different from creation and not already covered
        if (this.ztouchTimestamp !== this.zcreationTimestamp) {
            // Check if we already have an event at this exact timestamp
            const existingEvent = events.find(event => event.date === this.ztouchTimestamp);

            // Calculate time difference to avoid showing modifications that are essentially the same moment
            const creationTime = new Date(this.zcreationTimestamp).getTime();
            const touchTime = new Date(this.ztouchTimestamp).getTime();
            const timeDifferenceSeconds = Math.abs(touchTime - creationTime) / 1000;

            // Only add modification if:
            // 1. Not already covered by another event
            // 2. Time difference is more than 5 seconds (meaningful modification)
            if (!existingEvent && timeDifferenceSeconds > 5) {
                events.push({
                    status: 'MODIFICATION',
                    date: this.ztouchTimestamp,
                    label: 'Dernière modification'
                });
            }
        }

        // Sort events by date
        return events.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    }

    /**
     * Converts request model to DTO for API requests
     */
    toDTO(): IMedicalRequest {
        return {
            entityId: this.entityId,
            zTouchTimestamp: this.zTouchTimestamp,
            zCreationTimestamp: this.zCreationTimestamp,
            status: this.status,
            speciality: this.speciality,
            requestType: this.requestType,
            content: this.content.toDTO(),
            isRead: this.isRead,
            clinicianId: this.clinicianId,
            patientId: this.patientId,
            handleBy: this.handleBy,
            clinician: this.clinician,
            patient: this.patient,
            expertAssigned: this.expertAssigned,
            requestStatusDates: this.requestStatusDates,
            ztouchTimestamp: this.ztouchTimestamp,
            zcreationTimestamp: this.zcreationTimestamp
        };
    }

    /**
     * Checks if request matches search term
     */
    matchesSearch(searchTerm: string): boolean {
        if (!searchTerm) return true;
        
        const term = searchTerm.toLowerCase();
        const searchableFields = [
            this.patientFullName.toLowerCase(),
            this.clinicianFullName.toLowerCase(),
            this.summary.toLowerCase(),
            this.requestType.toLowerCase(),
            this.speciality.toLowerCase(),
            this.statusDisplay.toLowerCase(),
        ];
        
        return searchableFields.some(field => field.includes(term));
    }

    /**
     * Clone request instance
     */
    clone(): ExpertRequest {
        return new ExpertRequest(this.toDTO());
    }
}